import { money } from "@/lib/format";

const focusSibling = (input, col, dir) => {
  const table = input.closest("table");
  if (!table) return;
  const cells = [...table.querySelectorAll(`input[data-col="${col}"]:not([disabled])`)];
  const next = cells[cells.indexOf(input) + dir];
  if (next) { next.focus(); next.select?.(); }
};

// Left/Right always jump to the neighbouring editable cell in the same row (grid lock — no caret moves inside the number).
const focusInRow = (input, dir) => {
  const row = input.closest("tr");
  if (!row) return;
  const cells = [...row.querySelectorAll("input:not([disabled]):not([type=checkbox])")];
  const next = cells[cells.indexOf(input) + dir];
  if (next) { next.focus(); next.select?.(); }
};

export const gridKeyNav = (e, col) => {
  if (e.key === "Enter" || e.key === "ArrowDown") { e.preventDefault(); focusSibling(e.target, col, 1); }
  else if (e.key === "ArrowUp") { e.preventDefault(); focusSibling(e.target, col, -1); }
  else if (e.key === "ArrowRight") { e.preventDefault(); focusInRow(e.target, 1); }
  else if (e.key === "ArrowLeft") { e.preventDefault(); focusInRow(e.target, -1); }
};

// Zero from the library / a new line shows as blank (placeholder 0); typed "0" stays visible.
export const blankZero = (v) => (v === 0 || v == null ? "" : v);

const ACCENT = { amber: "border-amber-300", sky: "border-sky-300", default: "border-slate-200" };

export function GridCell({ col, value, onChange, step = "0.01", min, max, disabled, testId, title, placeholder = "0", accent = "default", className = "" }) {
  return (
    <input
      type="number" step={step} min={min} max={max} data-col={col} value={value ?? ""} disabled={disabled} title={title} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      onFocus={(e) => e.target.select()}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => gridKeyNav(e, col)}
      className={`h-7 w-full rounded-md border bg-background px-1.5 text-right text-xs tabular-nums outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none focus:border-brand focus:ring-1 focus:ring-brand disabled:cursor-not-allowed disabled:opacity-50 ${ACCENT[accent] || ACCENT.default} ${className}`}
      data-testid={testId}
    />
  );
}

export function Calc({ value, kind = "money", strong, testId, className = "" }) {
  let text = "—";
  if (value != null && Number.isFinite(value)) {
    text = kind === "money" ? money(value) : kind === "pct" ? `${value.toFixed(1)}%` : kind === "hours" ? value.toFixed(2) : String(value);
  }
  return (
    <span className={`block text-right text-xs tabular-nums ${strong ? "font-semibold text-slate-900" : "text-slate-500"} ${className}`} data-testid={testId}>{text}</span>
  );
}

export function GmCalc({ value, testId }) {
  const tone = value == null ? "text-slate-400" : value >= 30 ? "text-emerald-700" : value >= 15 ? "text-amber-600" : "text-red-600";
  return <span className={`block text-right text-xs font-semibold tabular-nums ${tone}`} data-testid={testId}>{value == null ? "—" : `${value.toFixed(1)}%`}</span>;
}

const TONES = {
  SUB: "bg-sky-100 text-sky-700",
  FLAT: "bg-amber-100 text-amber-700",
  Quote: "bg-amber-100 text-amber-700",
  Incomplete: "bg-slate-100 text-slate-500",
  internal: "bg-slate-100 text-slate-500 font-medium",
};
export function Tag({ kind, testId, children }) {
  return <span className={`shrink-0 rounded px-1.5 py-px text-[10px] font-semibold leading-4 ${TONES[kind] || TONES.Incomplete}`} data-testid={testId}>{children || kind}</span>;
}
